import React, { useState, useCallback } from 'react';
import { UserCheck } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Button, ButtonProps } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  consents,
  type ConsentItem,
  ConsentConfig,
  CarescoutConsentConfig,
} from '@shared/consents';
import ReactMarkdown from 'react-markdown';

interface ConsentCheckboxItemProps {
  item: ConsentItem;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

const ConsentCheckboxItem = ({ item, checked, onChange }: ConsentCheckboxItemProps) => (
  <Card>
    <CardContent className="p-4">
      <div className="flex-1 space-y-1">
        <Label
          htmlFor={item.id}
          className="cursor-pointer text-sm font-medium text-gray-900"
        >
          {item.title}
        </Label>
        <ReactMarkdown
          components={{
            p: ({ node, ...props }) => (
              <p
                {...props}
                className="text-xs text-gray-700"
              >
                {props.children}
              </p>
            ),
            ul: ({ node, ...props }) => (
              <ul
                className="list-disc pl-4 text-xs text-gray-700"
                {...props}
              />
            ),
            li: ({ node, ...props }) => (
              <li
                className="my-1 text-xs text-gray-700"
                {...props}
              />
            ),
          }}
        >
          {item?.formattedDescription ? item?.formattedDescription?.trim() : item.description}
        </ReactMarkdown>
      </div>
      <div className="mt-2 flex items-center gap-3">
        <Checkbox
          data-testid="application-consent-checkbox"
          id={item.id}
          checked={checked}
          onCheckedChange={onChange}
        />
        <span className="text-xs text-gray-700">{item.checkbox}</span>
      </div>
    </CardContent>
  </Card>
);

function PDFViewer() {
  const pdfUrl = 'https://drive.google.com/file/d/15Mnd7X5qQ1weuCninjZKkg2xZp7eM3SO/preview';
  return (
    <object
      data={pdfUrl}
      type="application/pdf"
      width="100%"
      height="350px"
    >
      <p>
        Your browser doesn't support PDFs. <a href={pdfUrl}>Download the PDF</a>
      </p>
    </object>
  );
}

interface RecordConsentsProps {
  userType: 'advisor' | 'client' | 'partnerClient';
  onSubmit: (consentIds: string[]) => void;
  submitButtonProps?: ButtonProps;
  consentType: 'submit' | 'start';
  showPdf?: boolean;
  isCareScout: boolean;
}

export function RecordConsents({
  userType,
  onSubmit,
  submitButtonProps,
  consentType,
  showPdf = false,
  isCareScout,
}: RecordConsentsProps) {
  const config = isCareScout ? CarescoutConsentConfig : ConsentConfig;
  const advisorConsents = config[consentType].advisor;
  const clientConsents = config[consentType].client;
  const consentsToCheck = userType === 'advisor' ? advisorConsents : clientConsents;
  // Get consents appropriate for the user type
  const userConsents = Object.values(consents).filter(consent =>
    consentsToCheck.includes(consent.id),
  );

  const [consentStates, setConsentStates] = useState<Record<string, boolean>>(() =>
    userConsents.reduce((acc, item) => ({ ...acc, [item.id]: false }), {}),
  );

  const allConsentsChecked = Object.values(consentStates).every(Boolean);

  const handleConsentChange = useCallback((consentId: string, checked: boolean) => {
    setConsentStates(prev => ({ ...prev, [consentId]: checked }));
  }, []);

  const handleSubmit = useCallback(() => {
    if (!allConsentsChecked) return;

    const checkedConsentIds = userConsents
      .filter(consent => consentStates[consent.id])
      .map(consent => consent.id);

    onSubmit(checkedConsentIds);
  }, [allConsentsChecked, userConsents, consentStates, onSubmit]);

  const getUserTypeLabel = () => {
    const userTypeLabels = {
      advisor: 'Advisor',
      client: 'Primary Client',
      partnerClient: 'Partner Client',
    };
    return userTypeLabels[userType];
  };

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        {userConsents.map(item => (
          <ConsentCheckboxItem
            key={item.id}
            item={item}
            checked={consentStates[item.id] || false}
            onChange={checked => handleConsentChange(item.id, checked)}
          />
        ))}
      </div>
      {showPdf && <PDFViewer />}
      <div className="pt-4">
        <Button
          data-testid="application-consent-submit-button"
          onClick={handleSubmit}
          disabled={!allConsentsChecked}
          className="w-full gap-2"
          size="lg"
          {...submitButtonProps}
        >
          <UserCheck className="h-5 w-5" />
          Record {getUserTypeLabel()} Consents
        </Button>
      </div>
    </div>
  );
}
